import moment from "moment";
import {FilterTypes} from "../../../constans/filter";
import {getFilterParams} from "../../../utils/FilterUtils";

// limit przedziałów jednego poziomu w "Własnych grupach" - po kolorze palety kategorii na przedział
// (categoricalColors, ten sam na histogramie); "Każda wartość" nie ma limitu (stronicowanie)
export const MAX_RULE_GROUPS = 8;

// słownik async (duckDB) to wartości tekstowe, także tablice (np. knury inseminacji) - reguły jak dla tekstu
const RULE_TYPES = [
    FilterTypes.NUMBER,
    FilterTypes.DATE,
    FilterTypes.STRING,
    FilterTypes.ASYNC_DICTIONARY,
];

// okres grupowania kolumn z datą przy "Każdej wartości"; wartość idzie do API w poziomie (`DateUnit`)
export const DATE_UNITS = ["day", "week", "month"];

export const DEFAULT_DATE_UNIT = "day";

export const DATE_UNIT_LABELS = {
    day: "day",
    week: "week",
    month: "month",
};

/**
 * Czy poziom grupowania ma wybór okresu (kolumna z datą).
 * @param {string} filterType typ filtra kolumny
 */
export const isDateLevel = (filterType) => filterType === FilterTypes.DATE;

/**
 * Etykieta grupy z okresem tygodnia / miesiąca. Klucz grupy to początek okresu (data ISO).
 * Dzień formatuje kolumna (jak dotąd) - zwraca null.
 * @param {*} key klucz grupy z odpowiedzi
 * @param {"day"|"week"|"month"} unit
 * @param {function} t tłumaczenia
 * @returns {string|null}
 */
export function formatDateUnitKey(key, unit, t) {
    // tylko jawny tydzień / miesiąc - bez okresu (kolumna bez daty) klucz formatuje kolumna
    if (
        key === null ||
        key === undefined ||
        !["week", "month"].includes(unit)
    ) {
        return null;
    }
    // klucz z serwera w UTC ("2026-03-01T00:00:00.000Z") - w czasie lokalnym przesunąłby dzień
    const date = moment.utc(key);
    if (!date.isValid()) return null;
    if (unit === "week") {
        return `${t("week")} ${date.isoWeek()}, ${date.isoWeekYear()}`;
    }
    return date.format("MMMM YYYY");
}

/**
 * Okres z ustawień - nieznany albo brak to domyślny dzień.
 * @param {string} [unit]
 * @returns {"day"|"week"|"month"}
 */
export function normalizeDateUnit(unit) {
    return DATE_UNITS.includes(unit) ? unit : DEFAULT_DATE_UNIT;
}

/**
 * Reguła grupowania - wyrażenia jak w filtrze kolumny, "|" oddziela kolejne grupy
 * (w filtrze tekstowym "|" znaczy "lub" i rozbija go serwer; tu dzielimy na froncie).
 * Każdy fragment to warunek w formacie `Filter` API, więc backend buduje go tym samym kodem.
 * @param {string} type typ filtra kolumny (FilterTypes)
 * @param {string} expression wpis użytkownika, np. "<95 | [95 - 105] | >105"
 * @returns {{buckets: Array<{type: string, value: string|string[]}>, labels: string[], errors: Array<{index: number, segment: string, reason: "invalid"|"wildcardNotAlone"|"tooMany"|"unsupported"}>}}
 * `labels` - treść fragmentu każdego przedziału (etykieta grupy),
 * `index` błędu to pozycja fragmentu wśród niepustych fragmentów (-1 - całe wyrażenie)
 */
export function parseGroupRule(type, expression) {
    const buckets = [];
    const labels = [];
    const errors = [];
    if (!RULE_TYPES.includes(type)) {
        errors.push({index: -1, segment: expression, reason: "unsupported"});
        return {buckets, labels, errors};
    }
    // puste fragmenty pomijamy - np. "|" na końcu w trakcie pisania
    const segments = expression
        .split("|")
        .map((segment) => segment.trim())
        .filter(Boolean);
    segments.forEach((segment, index) => {
        // "?" w dacie serwer rozwija do wzorca - jak w filtrze, tylko jako jedyne wyrażenie
        if (
            type === FilterTypes.DATE &&
            segment.includes("?") &&
            segments.length > 1
        ) {
            errors.push({index, segment, reason: "wildcardNotAlone"});
            return;
        }
        const params = getFilterParams(type, segment);
        if (!params) {
            errors.push({index, segment, reason: "invalid"});
            return;
        }
        if (buckets.length === MAX_RULE_GROUPS) {
            errors.push({index, segment, reason: "tooMany"});
            return;
        }
        buckets.push(params);
        labels.push(segment);
    });
    return {buckets, labels, errors};
}

/**
 * Poziom grupowania w `GroupFields` API. Bez reguły (albo z błędną) - samo pole, czyli każda wartość;
 * kolumna z datą dostaje wtedy zawsze okres (`DateUnit`: day / week / month, domyślnie day).
 * Z regułą - przedziały: klucz grupy w odpowiedzi i `GroupKeys` to indeks przedziału,
 * N (liczba przedziałów) to "Pozostałe", N+1 to puste wartości.
 * @param {string} field pole grupowania (filterColumn || field)
 * @param {string} filterType typ filtra kolumny
 * @param {{expression: string, names: string[], rest: boolean, restName?: string, nullGroup: boolean, nullName?: string}} [rule]
 * @param {"day"|"week"|"month"} [dateUnit] okres dla kolumny z datą
 * @param {{toRaw: function(number): number}} [inputConverter] liczby we wpisie są w jednostce wyświetlania
 *        kolumny (np. kg) - przeliczenie na wartości w danych (np. g)
 * @returns {string|{field: string, Buckets: Array, Rest: boolean, NullGroup: boolean}|{field: string, DateUnit: string}}
 */
export function buildGroupLevel(
    field,
    filterType,
    rule,
    dateUnit,
    inputConverter
) {
    const valueLevel = isDateLevel(filterType)
        ? {field, DateUnit: normalizeDateUnit(dateUnit)}
        : field;
    if (!rule) return valueLevel;
    const {buckets, errors} = parseGroupRule(filterType, rule.expression);
    if (errors.length > 0 || buckets.length === 0) return valueLevel;
    return {
        field,
        Buckets: convertBuckets(filterType, buckets, inputConverter?.toRaw),
        Rest: !!rule.rest,
        NullGroup: !!rule.nullGroup,
    };
}

/**
 * Co oznacza klucz grupy z poziomu z regułą.
 * @param {Object} rule reguła poziomu
 * @param {string} filterType typ filtra kolumny
 * @param {number|string} key klucz grupy z odpowiedzi (indeks)
 * @returns {{kind: "bucket"|"rest"|"empty", label?: string, name: string}|null} `label` tylko dla przedziału
 */
export function describeGroupKey(rule, filterType, key) {
    const {labels} = parseGroupRule(filterType, rule.expression);
    const index = Number(key);
    if (index >= 0 && index < labels.length) {
        return {
            kind: "bucket",
            label: labels[index],
            name: rule.names?.[index] ?? "",
        };
    }
    if (index === labels.length) {
        return {kind: "rest", name: rule.restName ?? ""};
    }
    if (index === labels.length + 1) {
        return {kind: "empty", name: rule.nullName ?? ""};
    }
    return null;
}

// krótsze wyrażenie widać na chipie poziomu w całości, dłuższe zastępuje liczba grup
const SHORT_EXPRESSION_LENGTH = 12;

/**
 * Zapis reguły na chipie poziomu w "Grupuj według".
 * @returns {{expression: string}|{count: number, rest: boolean}}
 */
export function summarizeGroupRule(rule, filterType) {
    const expression = rule.expression.trim();
    if (expression.length <= SHORT_EXPRESSION_LENGTH) return {expression};
    const {buckets} = parseGroupRule(filterType, expression);
    return {count: buckets.length, rest: !!rule.rest};
}

/**
 * Liczby przedziałów przeliczone funkcją (jednostka wpisu <-> jednostka danych); inne typy bez zmian.
 * @param {string} filterType
 * @param {Array<{type: string, value: string|string[]}>} buckets
 * @param {function(number): number} [convert]
 */
function convertBuckets(filterType, buckets, convert) {
    if (!convert || filterType !== FilterTypes.NUMBER) return buckets;
    const convertValue = (value) => String(convert(Number(value)));
    return buckets.map((bucket) => ({
        ...bucket,
        value: Array.isArray(bucket.value)
            ? bucket.value.map(convertValue)
            : convertValue(bucket.value),
    }));
}

// wartość przedziału jak w polu filtra - data w formacie lokalnym, który czyta parser filtra
function formatBucketValue(filterType, value) {
    if (filterType !== FilterTypes.DATE) return String(value);
    return moment(value, moment.HTML5_FMT.DATE).format("L");
}

/**
 * Przedziały (np. z propozycji API) -> wyrażenie w składni filtra kolumny, które `parseGroupRule`
 * czyta z powrotem do tych samych przedziałów.
 * @param {string} filterType typ filtra kolumny
 * @param {Array<{type: string, value: string|string[]}>} buckets wartości jak w danych
 * @param {{fromRaw: function(number): number}} [inputConverter] wpis w jednostce wyświetlania kolumny
 * @returns {string} np. "<=15 | (15 - 18] | >18"
 */
export function serializeGroupBuckets(filterType, buckets, inputConverter) {
    return convertBuckets(filterType, buckets, inputConverter?.fromRaw)
        .map(({type, value}) => {
            if (Array.isArray(value)) {
                const [from, to] = value.map((item) =>
                    formatBucketValue(filterType, item)
                );
                return `${type[0]}${from} - ${to}${type[1]}`;
            }
            const text = formatBucketValue(filterType, value);
            // "=" i fragment tekstu to sama wartość - jak w filtrze
            return type === "=" || type === "LIKE" ? text : `${type}${text}`;
        })
        .join(" | ");
}

// kolejność grup poziomu "Każda wartość" (własne grupy mają kolejność wyrażeń), w kolejności przełączania
// nagłówkiem; grupy zawsze mają jakąś kolejność, więc domyślnie rosnąco po wartości
export const GROUP_SORTS = ["asc", "desc", "countAsc", "countDesc"];

export const DEFAULT_GROUP_SORT = "asc";

// nazwy (klucze tłumaczeń) i ikony kolejności grup (Font Awesome) - modal i nagłówek kolumny poziomu
export const GROUP_SORT_LABELS = {
    asc: "ascending",
    desc: "descending",
    countAsc: "rowsCountAscending",
    countDesc: "rowsCountDescending",
};

export const GROUP_SORT_ICONS = {
    asc: "fa-arrow-down-a-z",
    desc: "fa-arrow-down-z-a",
    countAsc: "fa-arrow-down-short-wide",
    countDesc: "fa-arrow-down-wide-short",
};

/**
 * Zapisana kolejność grup -> jedna z GROUP_SORTS. Brak - domyślna; "count" ze starszych widoków - wiersze malejąco.
 * @param {string} [sort]
 * @returns {"asc"|"desc"|"countAsc"|"countDesc"}
 */
export function normalizeGroupSort(sort) {
    if (sort === "count") return "countDesc";
    return GROUP_SORTS.includes(sort) ? sort : DEFAULT_GROUP_SORT;
}

/**
 * Kolejność grup po kliknięciu nagłówka kolumny poziomu:
 * rosnąco -> malejąco -> wiersze rosnąco -> wiersze malejąco -> rosnąco.
 * @param {string} [sort]
 * @returns {"asc"|"desc"|"countAsc"|"countDesc"}
 */
export function nextGroupSort(sort) {
    const index = GROUP_SORTS.indexOf(normalizeGroupSort(sort));
    return GROUP_SORTS[(index + 1) % GROUP_SORTS.length];
}

/**
 * `GroupSort` API dla poziomu "Każda wartość". Wartości `by` ("key" / "count") do potwierdzenia z backendem.
 * @param {string} [sort] zapisana kolejność (brak - rosnąco)
 * @param {Object} column nagłówek kolumny poziomu (customSort)
 * @returns {{by: string, direction: string, customSort?: string}}
 */
export function buildGroupSort(sort, column) {
    switch (normalizeGroupSort(sort)) {
        case "countAsc":
            return {by: "count", direction: "ASC"};
        case "countDesc":
            return {by: "count", direction: "DESC"};
        case "desc":
            return {
                by: "key",
                direction: "DESC",
                customSort: column.customSort,
            };
        default:
            return {by: "key", direction: "ASC", customSort: column.customSort};
    }
}
